/* =============================================================================
   VIEWS / ADMIN / STRIPE-BON-CATCHUP — GoBD-Bon für webhook-bezahlte Orders (S234 T2)
   =============================================================================
   Problem: Stripe-Zahlungen werden serverseitig im `stripe-webhook` verbucht
   (payment_status=paid, Rechnungsnr., TSE-Signatur) — aber die Bon-Archivierung
   (`_ensureBonArchived`) + Beleg-Mail existieren NUR im Cockpit-Client
   (orderMarkPaid / correction / driver-terminal). Webhook-bezahlte Orders
   durchlaufen keinen dieser Pfade → `bon_archive` = 0 Rows (GoBD §147-AO-Lücke).

   Lösung (bewusst CLIENT-seitig — html2canvas ist browser-only): sobald ein
   Cockpit online ist, erkennt es stripe-bezahlte Orders OHNE Bon und holt die
   GARANTIERTE Archivierung (idempotent) + Beleg-Mail einmalig nach. Trigger:
   Boot-Scan + `order:realtime`/`order:paid`-Events (debounced).

   Idempotenz (S238-HF4 — server-seitig statt Cache-basiert):
     · Kandidaten: RPC `stripe_bon_catchup_candidates()` (phase_170) liefert die
       stripe-bezahlten Orders OHNE `bon_archive`-Row + das Flag
       `has_receipt_mail`. Der frühere Weg über den `loadBonArchive()`-Cache war
       der Bug: bootete ein Cockpit mit leerem Cache (Hydrate-Timeout beim
       DB-Ausfall 25.08.), galten längst belegte Orders als „ohne Bon" → zweite
       Beleg-Mail an den Gast (empirisch: `ord_1787582575_cc94c`, 2× order_receipt
       bei genau 1 Bon).
     · Bon: `_ensureBonArchived(..., { insertOnly: true })` schreibt per INSERT.
       Der UNIQUE-Constraint (restaurant_id, order_id) ist der Schiedsrichter —
       nur wer die Zeile NEU anlegt (`archived && !alreadyArchived`), ist
       Ersteller. Konflikt → kein Mail.
     · Mail: nur der Bon-Ersteller versendet, und nur wenn die RPC KEINE
       vorhandene `order_receipt`-Mail meldet. Der geräte-lokale Marker bleibt
       als dritte Bremse (greift nur auf demselben Gerät — deshalb war er allein
       nie ausreichend).
     · Boot-Scan läuft erst, wenn `bonArchive.hydrate()` erfolgreich war
       (bootstrap-Flag) — ein Scan auf halb-initialisiertem Zustand bringt nichts.
============================================================================ */

import { _ensureBonArchived } from './orders-receipt-actions.js';
import { sendOrderReceiptEmail } from '../../core/email-templates.js';
// S246 — derselbe Pseudo-Order-Bau wie beim Bar-Teilbeleg. Kein zweiter
// Bon-Renderer, kein zweites Format.
import { buildReceiptPseudoOrder } from './orders-split.js';

const _inflight = new Set();
const MAILED_KEY = 'gg.stripeCatchupMailed';
let _wired = false;
let _debounceTimer = null;
let _fallbackTimer = null;
// S235-HF3 — Fallback-Scan-Verzögerung: beim Boot ist der frühe Scan (1,5 s) oft
// VOR der Order-Hydration (Realtime-Initial-Load); dieser spätere Scan holt den
// Backlog nach, falls kein order:realtime-Event mehr kommt. Idempotenz vorhanden.
const HYDRATION_FALLBACK_MS = 12000;

function _state() {
  return (window.App && window.App.storage && window.App.storage._getInternalState
    && window.App.storage._getInternalState()) || { orders: [] };
}

function _mailedMarker() {
  try { return new Set(JSON.parse(localStorage.getItem(MAILED_KEY) || '[]')); }
  catch (_) { return new Set(); }
}
function _rememberMailed(id) {
  try {
    const s = _mailedMarker(); s.add(String(id));
    localStorage.setItem(MAILED_KEY, JSON.stringify([...s].slice(-200)));
  } catch (_) { /* Storage voll — nicht-fatal */ }
}

/** Pure — ist die Order stripe-bezahlt, aber (im übergebenen Cache) noch ohne
 *  Bon? Korrekturbelege sind ausgenommen (die haben ihren eigenen Pfad).
 *  S238-HF4: NICHT mehr die Kandidaten-Quelle (das war der Doppel-Mail-Bug),
 *  sondern nur noch ein Vorfilter für die Order-Objekte, die zur RPC-Liste
 *  gehören — die Wahrheit über „hat schon einen Bon" kommt aus der DB. */
export function isStripePaidWithoutBon(order, bonCache) {
  if (!order || !order.id) return false;
  const pm = order.paymentMethod || order.payment_method;
  const ps = order.paymentStatus || order.payment_status;
  if (pm !== 'stripe' || ps !== 'paid') return false;
  if (order.isCorrection || order.is_correction) return false;
  const cache = bonCache || {};
  return !cache[order.id];
}

/**
 * S238-HF4 — Pure: RPC-Antwort → zu bearbeitende Kandidaten. Die RPC filtert
 * bereits server-seitig (stripe/paid/keine Korrektur/kein Bon); hier bleibt nur
 * die Normalisierung + der Mail-Gate-Entscheid je Zeile.
 * @returns {Array<{orderId: string, orderNumber: string, mayMail: boolean}>}
 */
export function candidatesFromRpc(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter(r => r && (r.order_id || r.orderId))
    .map(r => ({
      orderId:     r.order_id || r.orderId,
      orderNumber: r.order_number || r.orderNumber || '',
      // Liegt schon eine order_receipt-Mail in der Outbox, wird der Bon zwar
      // (falls nötig) noch archiviert, aber NIE ein zweites Mal gemailt.
      mayMail:     !(r.has_receipt_mail === true || r.hasReceiptMail === true)
    }));
}

/** Holt Bon + Beleg-Mail für EINE stripe-bezahlte Order nach (idempotent).
 *  @param {object} order  Order-Objekt
 *  @param {object} [opts] { mayMail: boolean } — Mail-Gate aus der RPC (default true) */
export async function catchUpStripeBonFor(order, opts = {}) {
  const mayMail = opts.mayMail !== false;
  if (!order || !order.id || _inflight.has(order.id)) return { archived: false };
  _inflight.add(order.id);
  try {
    // Fresh + Items laden → preorderSnapshot bauen (identisch zu orderMarkPaid,
    // damit der Bon-Renderer die Positions-Zeilen zeigt).
    const storage = window.App && window.App.storage && window.App.storage.orders;
    let fresh = order;
    try {
      if (storage && typeof storage.getFresh === 'function') {
        fresh = (await storage.getFresh(order.id).catch(() => null)) || order;
      }
      const items = (storage && typeof storage.listItems === 'function')
        ? await storage.listItems(order.id).catch(() => []) : [];
      if (items && items.length) {
        fresh = { ...fresh, preorderSnapshot: {
          version: 2,
          items: items.map(it => ({
            name:             it.nameSnapshot || it.name_snapshot,
            qty:              it.qty,
            unit_price_cents: it.unitPriceCents || it.unit_price_cents,
            line_total_cents: it.lineTotalCents || it.line_total_cents,
            tax_rate:         it.taxRate || it.tax_rate,
            note:             it.notes || (it.customizations && it.customizations.note)
          }))
        } };
      }
    } catch (_) { /* Bon zeigt ggf. „Keine Positionen" — nicht-fatal */ }

    // S238-HF4: INSERT entscheidet über die Ersteller-Rolle, nicht der Cache.
    const res = await _ensureBonArchived(fresh, { source: 'stripe-catchup', insertOnly: true });

    // Beleg-Mail nur, wenn ALLE drei Bremsen offen sind:
    //   1. dieser Aufruf hat den Bon wirklich neu angelegt (INSERT ohne Konflikt),
    //   2. die DB meldet keine bestehende order_receipt-Mail (RPC-Flag),
    //   3. dieses Gerät hat für die Order noch nicht gemailt (localStorage).
    if (res && res.archived && !res.alreadyArchived) {
      if (!mayMail) {
        console.info('[stripe-catchup] Beleg-Mail übersprungen — order_receipt liegt bereits in der Outbox:', order.id);
      } else {
        const mailed = _mailedMarker();
        if (!mailed.has(String(order.id))) {
          _rememberMailed(order.id);
          try { await sendOrderReceiptEmail(fresh); }
          catch (e) { console.warn('[stripe-catchup] Beleg-Mail fehlgeschlagen (nicht-fatal):', e); }
        }
      }
    }
    return res || { archived: false };
  } finally {
    _inflight.delete(order.id);
  }
}

/**
 * S238-HF4 — War der Bon-Archiv-Hydrate beim Boot erfolgreich? Ein Scan auf
 * halb-initialisiertem Zustand (Hydrate im Timeout, wie beim DB-Ausfall am
 * 25.08.) darf nicht laufen: `_ensureBonArchived` würde für JEDE Order den
 * vollen Bon-Bau anwerfen. Die DB-Idempotenz (INSERT + RPC) fängt den Schaden
 * inzwischen ab, die Arbeit sparen wir uns trotzdem.
 * Flag setzt bootstrap.js (`hydrateArchivesFromDb`).
 */
function _bonArchiveHydrated() {
  return !!(window.App && window.App.storage && window.App.storage._archiveHydrateOk
    && window.App.storage._archiveHydrateOk.bonArchive === true);
}

/** Kandidaten server-seitig holen (phase_170). Liefert `null`, wenn die RPC
 *  nicht verfügbar ist (Migration noch nicht appliziert) — dann wird NICHT auf
 *  den Cache-Pfad zurückgefallen: lieber gar kein Catch-up als eine zweite
 *  Beleg-Mail an einen Gast. */
async function _fetchCandidates() {
  const supa = (window.App && window.App.supabase) || null;
  if (!supa || typeof supa.rpc !== 'function') return null;
  const { data, error } = await supa.rpc('stripe_bon_catchup_candidates');
  if (error) {
    console.warn('[stripe-catchup] stripe_bon_catchup_candidates nicht verfügbar — Scan übersprungen:', error.message || error);
    return null;
  }
  return candidatesFromRpc(data);
}

/** Order-Objekt zum Kandidaten finden: erst der lokale Cache, sonst frisch aus
 *  der DB (der Cache kann leer/veraltet sein — genau der HF-4-Fall). */
async function _resolveOrder(orderId) {
  const local = ((_state().orders) || []).find(o => o && o.id === orderId);
  if (local) return local;
  const storage = window.App && window.App.storage && window.App.storage.orders;
  if (storage && typeof storage.getFresh === 'function') {
    return await storage.getFresh(orderId).catch(() => null);
  }
  return null;
}

/**
 * S246/S248 — Bon-Nachlauf für TEILBELEGE (`order_receipts`).
 *
 * S246 baute ihn für die online bezahlten Gast-Belege: die entstehen im
 * Webhook, also ohne Cockpit, und hätten sonst nie einen Bon im Archiv. Der
 * Scan oben findet sie nicht — er sucht bezahlte ORDERS, und die Order bleibt
 * unbezahlt, solange der Rest offen ist.
 *
 * S248 hat gezeigt, dass die Annahme dahinter zu eng war: auch der Bar-/
 * Karten-Split archivierte nicht (live 0 von 17), obwohl das Belegausgabe-Modal
 * „Bereits automatisch archiviert" behauptete. Der Nachlauf fragt deshalb nach
 * dem, worauf es ankommt — **bezahlt**. Woher die Zahlung kam, ist für die
 * Archivpflicht (§147 AO) ohne Belang.
 *
 * Bewusst OHNE Beleg-Mail: ein Gast am Tisch hinterlässt keine Adresse, und der
 * Bar-Split hat noch nie gemailt. Hier geht es allein um das Archiv.
 *
 * Idempotent über dieselbe Bremse wie der Order-Pfad: `insertOnly` + der
 * UNIQUE-Constraint auf (restaurant_id, order_id) — die Pseudo-Order trägt die
 * Beleg-Id, also gibt es je Teilbeleg genau eine Archiv-Zeile.
 *
 * @param {object} [opts] `{ requireHydrated, sinceIso, all }`.
 *        `all: true` = ohne Zeitgrenze (der einmalige Nachtrag über die
 *        Historie). Ohne das läuft der Scan über die letzten drei Tage.
 */
export async function runReceiptBonCatchup(opts = {}) {
  try {
    const st = window.App && window.App.storage;
    if (!st || !st.bonArchive || !st.orders) return 0;
    if (typeof st.orders.listPaidReceiptsSince !== 'function') return 0;
    if (opts.requireHydrated && !_bonArchiveHydrated()) return 0;

    const sinceIso = opts.all ? null
      : (opts.sinceIso || new Date(Date.now() - 3 * 24 * 3600 * 1000).toISOString());
    const receipts = await st.orders.listPaidReceiptsSince(sinceIso).catch(() => []);
    if (!receipts || !receipts.length) return 0;

    const done = await _archivedReceiptIds(receipts.map(r => r.id));
    let n = 0;
    for (const r of receipts) {
      if (done.has(r.id) || _inflight.has(r.id)) continue;
      _inflight.add(r.id);
      try {
        const items  = await st.orders.listItemsForReceipt(r.id).catch(() => []);
        const parent = await _resolveOrder(r.orderId);
        const pseudo = buildReceiptPseudoOrder(r, items, parent || {});
        const res = await _ensureBonArchived(pseudo, {
          source: 'receipt-catchup', insertOnly: true
        });
        if (res && res.archived && !res.alreadyArchived) n++;
      } catch (e) {
        console.warn('[stripe-catchup] Teilbeleg-Bon fehlgeschlagen (nicht-fatal):', r.id, e);
      } finally {
        _inflight.delete(r.id);
      }
    }
    if (n > 0) console.info(`[stripe-catchup] ${n} Teilbeleg-Bon(s) nachgeholt.`);
    return n;
  } catch (e) {
    console.warn('[stripe-catchup] Teilbeleg-Scan übersprungen (nicht-fatal):', e);
    return 0;
  }
}

/**
 * S248 — einmaliger Nachtrag über die GESAMTE Historie.
 *
 * Bewusst eine ausdrückliche Aktion und kein stiller Boot-Lauf: es geht um das
 * Fiskal-Archiv, jede Zeile rendert einen Bon samt PDF, und wer nachträgt,
 * sollte sehen, wie viele es waren. Der laufende Scan bleibt beim
 * Drei-Tage-Fenster.
 *
 * Idempotent — ein zweiter Lauf legt nichts an (`insertOnly` + UNIQUE).
 */
export async function backfillReceiptBons() {
  return runReceiptBonCatchup({ all: true });
}

/** Welche dieser Belege haben schon eine Archiv-Zeile? Eine Abfrage, nicht N. */
async function _archivedReceiptIds(ids) {
  const supa = (window.App && window.App.supabase) || null;
  if (!supa || !ids.length) return new Set();
  const { data, error } = await supa.from('bon_archive').select('order_id').in('order_id', ids);
  if (error) {
    // Lieber nichts tun als blind archivieren — der INSERT-Guard traegt zwar,
    // aber der Bon-Bau je Beleg ist teuer.
    console.warn('[stripe-catchup] bon_archive-Abgleich fehlgeschlagen:', error.message || error);
    return new Set(ids);
  }
  return new Set((data || []).map(r => r.order_id));
}

/**
 * S250 (B4) — läuft hier überhaupt ein angemeldetes Cockpit?
 *
 * Der bisherige Riegel war `window.App.storage.bonArchive` — den gibt es auf
 * der Gast-Seite genauso. Also rief die anonyme Tisch-/Reservierungsseite bei
 * jedem Boot `stripe_bon_catchup_candidates` und bekam 401 (die RPC ist seit
 * phase_170 authenticated-only): abgefangen und harmlos, aber 15× je Sitzung
 * im API-Log. Genau dieses Rauschen macht ein ECHTES permission-denied
 * unsichtbar — im S249-Retest war es der einzige Treffer im Log, und es kostete
 * einen Prüfschritt, ihn als Vorbestand zu erkennen.
 *
 * `getSession()` liest den Cache, kein Netz. Fehlt die Sitzung beim Boot noch,
 * fällt dieser Lauf aus und der 12-s-Fallback bzw. das nächste
 * `order:realtime` holt ihn nach.
 */
async function _hasAuthSession() {
  const supa = (window.App && window.App.supabase) || null;
  if (!supa || !supa.auth || typeof supa.auth.getSession !== 'function') return false;
  try {
    const { data } = await supa.auth.getSession();
    return !!(data && data.session && data.session.access_token);
  } catch (_) { return false; }
}

/** Scan: alle stripe-bezahlten Orders ohne Bon (laut DB) nachholen. */
export async function runStripeBonCatchup(opts = {}) {
  try {
    // Nur im Cockpit sinnvoll (Bon-Archiv-Storage vorhanden).
    if (!window.App || !window.App.storage || !window.App.storage.bonArchive) return 0;
    // … und nur mit Anmeldung: beide Scans schreiben ins Fiskal-Archiv und
    // lesen Tabellen, die ein anonymer Gast gar nicht sehen darf.
    if (!(await _hasAuthSession())) return 0;
    if (opts.requireHydrated && !_bonArchiveHydrated()) {
      console.info('[stripe-catchup] Boot-Scan verschoben — bon_archive noch nicht hydratisiert.');
      return 0;
    }
    // S246/S248 — Teilbelege laufen unabhaengig von den Order-Kandidaten: die
    // Order bleibt unbezahlt, solange der Rest offen ist, also taucht sie in der
    // RPC gar nicht auf.
    const guestBons = await runReceiptBonCatchup(opts);

    const candidates = await _fetchCandidates();
    if (!candidates || candidates.length === 0) return guestBons;
    let n = 0;
    for (const c of candidates) {
      const order = await _resolveOrder(c.orderId);
      if (!order) continue;
      const res = await catchUpStripeBonFor(order, { mayMail: c.mayMail });
      if (res && res.archived && !res.alreadyArchived) n++;
    }
    if (n > 0) console.info(`[stripe-catchup] ${n} Stripe-Bon(s) nachgeholt.`);
    return n + guestBons;
  } catch (e) {
    console.warn('[stripe-catchup] Scan übersprungen (nicht-fatal):', e);
    return 0;
  }
}

function _scheduleScan() {
  if (_debounceTimer) return;
  // S238-HF4: `requireHydrated` — Boot-/Event-Scans warten auf einen
  // erfolgreichen bon_archive-Hydrate; der 12-s-Fallback läuft ohne das Gate,
  // damit ein einmal fehlgeschlagener Hydrate den Catch-up nicht dauerhaft
  // blockiert (die DB-Idempotenz trägt dort).
  _debounceTimer = setTimeout(() => {
    _debounceTimer = null;
    runStripeBonCatchup({ requireHydrated: true });
  }, 1500);
}

/**
 * Einmalige Verdrahtung (Cockpit): Boot-Scan + Live-Trigger auf order:realtime /
 * order:paid. Idempotent (nur einmal subscriben). No-Op ohne Bon-Archiv-Storage
 * (Gast-Kontext) oder Event-Bus.
 */
export function initStripeBonCatchup() {
  if (_wired) return;
  if (typeof window === 'undefined') return;
  if (!window.App || !window.App.storage || !window.App.storage.bonArchive) return;
  if (!window.App.events || typeof window.App.events.on !== 'function') return;
  _wired = true;
  try {
    window.App.events.on('order:realtime', _scheduleScan);
    window.App.events.on('order:paid',     _scheduleScan);
  } catch (_) { /* Event-Bus-API weicht ab — Boot-Scan greift trotzdem */ }
  // Initialer Scan (Orders evtl. schon hydratisiert; sonst greift der erste
  // order:realtime-Trigger).
  _scheduleScan();
  // S235-HF3 — Fallback-Scan NACH der Order-Hydration: der frühe Scan läuft beim
  // Boot oft, bevor der Realtime-Initial-Load die Orders in den State gefüllt hat;
  // ohne späteres order:realtime-Event bliebe ein Stripe-Bon-Backlog liegen.
  if (!_fallbackTimer) {
    _fallbackTimer = setTimeout(() => { _fallbackTimer = null; runStripeBonCatchup(); }, HYDRATION_FALLBACK_MS);
  }
}

/** Test-Hook: Verdrahtungs-Guard zurücksetzen. */
export function _resetStripeBonCatchupForTests() {
  _wired = false;
  if (_debounceTimer) { clearTimeout(_debounceTimer); _debounceTimer = null; }
  if (_fallbackTimer) { clearTimeout(_fallbackTimer); _fallbackTimer = null; }
}
